"use client"

import { useState, useEffect, useCallback, useRef } from "react"
import { propertyService } from "@/services/api"
import { transformApiProperties } from "@/utils/transformers"
import { PriceOrderType } from "@/types/api"
import type { Property, PropertyQueryParams, City } from "@/types/api"

interface FilterState {
  city: string
  priceSort: string
  searchText: string
}

interface UseInfinitePropertiesReturn {
  properties: Property[]
  loading: boolean
  loadingMore: boolean
  error: string | null
  totalCount: number
  hasNextPage: boolean
  loadMore: () => void
  refetch: () => Promise<void>
  clearError: () => void
  isInitialLoad: boolean
}

export function useInfiniteProperties(filters: FilterState, cities: City[] = []): UseInfinitePropertiesReturn {
  const [properties, setProperties] = useState<Property[]>([])
  const [loading, setLoading] = useState(false)
  const [loadingMore, setLoadingMore] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [totalCount, setTotalCount] = useState(0)
  const [currentPage, setCurrentPage] = useState(1)
  const [hasNextPage, setHasNextPage] = useState(false)
  const [isInitialLoad, setIsInitialLoad] = useState(true)

  const isLoadingRef = useRef(false)
  const isInitialLoadRef = useRef(true)
  const pageSize = 20

  // Convert v17 filters to API params
  const createApiParams = useCallback(
    (page: number): PropertyQueryParams => {
      const params: PropertyQueryParams = {
        pageIndex: page,
        pageSize,
      }

      // Handle city filter
      if (filters.city && filters.city !== "all") {
        // Find city ID by name from the cities array
        const city = cities.find(c => c.name === filters.city)
        if (city) {
          params.cityId = city.id
        }
      }

      // Handle search text
      if (filters.searchText.trim()) {
        params.searchvalue = filters.searchText.trim()
      }

      // Handle price sort
      if (filters.priceSort && filters.priceSort !== "none") {
        if (filters.priceSort === "low-to-high") {
          params.PriceOrderBy = PriceOrderType.PriceAscending
        } else if (filters.priceSort === "high-to-low") {
          params.PriceOrderBy = PriceOrderType.PriceDescending
        }
      }

      return params
    },
    [filters, cities],
  )

  // Load properties for a specific page
  const loadPropertiesPage = useCallback(
    async (page: number, append = false) => {
      if (isLoadingRef.current) return

      isLoadingRef.current = true

      if (!append) {
        setLoading(true)
      } else {
        setLoadingMore(true)
      }

      setError(null)

      try {
        const apiParams = createApiParams(page)
        // console.log("Loading properties with params:", apiParams)

        const response = await propertyService.getProperties(apiParams)
        const transformedProperties = transformApiProperties(response.items)

        if (append) {
          setProperties((prev) => [...prev, ...transformedProperties])
        } else {
          setProperties(transformedProperties)
        }

        setTotalCount(response.totalResults)
        setCurrentPage(page)
        setHasNextPage(page < response.pages)

        if (isInitialLoadRef.current) {
          isInitialLoadRef.current = false
          setIsInitialLoad(false)
        }
      } catch (err) {
        const errorMessage = err instanceof Error ? err.message : "Error al cargar las propiedades"
        setError(errorMessage)
        console.error("Error fetching properties:", err)
      } finally {
        setLoading(false)
        setLoadingMore(false)
        isLoadingRef.current = false
      }
    },
    [createApiParams],
  )

  // Load more properties (next page)
  const loadMore = useCallback(() => {
    if (hasNextPage && !isLoadingRef.current) {
      loadPropertiesPage(currentPage + 1, true)
    }
  }, [hasNextPage, currentPage, loadPropertiesPage])

  // Refetch from beginning
  const refetch = useCallback(async () => {
    setCurrentPage(1)
    await loadPropertiesPage(1, false)
  }, [loadPropertiesPage])

  const clearError = useCallback(() => {
    setError(null)
  }, [])

  // Effect to handle filter changes - using the v17 approach with debouncing
  useEffect(() => {
    // console.log("Filter effect triggered with filters:", filters)

    const timeoutId = setTimeout(() => {
      // console.log("Loading properties with debounced filters:", filters)
      setCurrentPage(1)
      setProperties([])
      loadPropertiesPage(1, false)
    }, 300) // 300ms debounce

    return () => clearTimeout(timeoutId)
  }, [filters.city, filters.priceSort, filters.searchText, loadPropertiesPage])

  return {
    properties,
    loading,
    loadingMore,
    error,
    totalCount,
    hasNextPage,
    loadMore,
    refetch,
    clearError,
    isInitialLoad,
  }
}
